import PropTypes from 'prop-types';

// Detect URLs (http/https or bare www.) in free text and render them as
// clickable links, keeping the surrounding text intact. Trailing punctuation
// (e.g. a period ending a sentence, or a closing paren) is kept out of the
// link target so it doesn't become part of the href.
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s]+)/gi;

/**
 * Render free text with any URLs turned into clickable anchors.
 * Returns a fragment so it can be dropped inside any styled wrapper and
 * inherit its typography.
 */
export default function LinkifiedText({ text, className = 'underline break-words' }) {
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;
  let match;
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const start = match.index;
    let url = match[0];
    const trailing = (url.match(/[.,;:!?)\]}'"»]+$/) || [''])[0];
    if (trailing) url = url.slice(0, url.length - trailing.length);
    // Guard against a "url" that was pure punctuation (shouldn't happen given
    // the regex, but keeps the loop from stalling on a zero-length match).
    if (!url) { URL_REGEX.lastIndex = start + match[0].length; continue; }

    if (start > lastIndex) parts.push(text.slice(lastIndex, start));
    const href = url.startsWith('http') ? url : `https://${url}`;
    parts.push(
      <a
        key={start}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={className}
      >
        {url}
      </a>
    );
    if (trailing) parts.push(trailing);
    lastIndex = start + match[0].length;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return <>{parts}</>;
}

LinkifiedText.propTypes = {
  text: PropTypes.string,
  className: PropTypes.string,
};
